/**
 * Granting a role.
 *
 * A grant is a role **in a scope, for a period, for a reason** — four things, and the dialog asks for all
 * four because each one is a question somebody will ask later.
 *
 * Scope is the one people get wrong. "No locations selected" does not mean "no scope": in this model an
 * empty scope IS the whole organisation. So organisation-wide is an explicit choice here, never the result
 * of forgetting to tick a box, and the server independently refuses it from anyone whose own granting
 * authority is limited to particular locations.
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { rpc, supabase } from '../../lib/supabase';
import { useIdempotencyKey } from '../../lib/idempotency';
import { Dialog } from '../../ui/overlays';
import { Checkbox, Field, Form, Input, Select, Textarea } from '../../ui/forms';
import { SectionLabel, cx } from '../../ui/primitives';
import { useLocationOptions } from './AccessPreview';
import type { RoleRow } from './types';

interface GrantResult {
  status: 'GRANTED' | 'UNCHANGED';
  grant_id: string;
  warnings?: string[];
}

export function GrantRoleDialog({
  open, onClose, accountId, accountLabel, onGranted,
}: {
  open: boolean;
  onClose: () => void;
  accountId: string;
  accountLabel: string;
  onGranted?: (result: GrantResult) => void;
}) {
  const queryClient = useQueryClient();
  const [roleCode, setRoleCode] = useState('');
  const [scope, setScope] = useState<'LOCATIONS' | 'ORGANIZATION'>('LOCATIONS');
  const [locationIds, setLocationIds] = useState<string[]>([]);
  const [locationTerm, setLocationTerm] = useState('');
  const [validTo, setValidTo] = useState('');
  const [reason, setReason] = useState('');

  const roles = useQuery({
    queryKey: ['iam-roles', 'active'],
    enabled: open,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('roles')
        .select('id,code,name,role_group,requires_mfa,is_system,status,permission_count,description,external_role_key')
        .eq('status', 'ACTIVE')
        .order('role_group')
        .order('name');
      if (error) throw error;
      return data as RoleRow[];
    },
  });
  const locations = useLocationOptions(open);

  const visibleLocations = useMemo(() => {
    const term = locationTerm.trim().toLowerCase();
    const all = locations.data ?? [];
    return term ? all.filter((l) => l.code.toLowerCase().includes(term) || l.name.toLowerCase().includes(term)) : all;
  }, [locations.data, locationTerm]);

  const role = (roles.data ?? []).find((r) => r.code === roleCode);

  const idem = useIdempotencyKey();
  const grant = useMutation({
    mutationFn: async () => {
      const args = {
        p_role_code: roleCode,
        p_user_account_id: accountId,
        p_api_client_id: null,
        p_location_ids: scope === 'ORGANIZATION' ? [] : locationIds,
        p_valid_to: validTo ? new Date(`${validTo}T23:59:59`).toISOString() : null,
        p_legal_entity_id: null,
        p_reason: reason.trim(),
      };
      return rpc<GrantResult>('grant_role', { ...args, p_idempotency_key: await idem.keyFor(args) });
    },
    onSuccess: async (result) => {
      idem.renew();
      await queryClient.invalidateQueries({ queryKey: ['users', '360', accountId] });
      await queryClient.invalidateQueries({ queryKey: ['iam-effective-access', accountId] });
      await queryClient.invalidateQueries({ queryKey: ['iam-users'] });
      onGranted?.(result);
      close();
    },
  });

  const close = () => {
    setRoleCode('');
    setScope('LOCATIONS');
    setLocationIds([]);
    setLocationTerm('');
    setValidTo('');
    setReason('');
    grant.reset();
    onClose();
  };

  const incomplete =
    !roleCode || reason.trim().length < 3 || (scope === 'LOCATIONS' && locationIds.length === 0);

  return (
    <Dialog open={open} onClose={close} title={`Grant a role to ${accountLabel}`} width="42rem">
      <Form
        onSubmit={() => grant.mutateAsync().then(() => undefined).catch(() => undefined)}
        submitLabel="Grant"
        onCancel={close}
        error={grant.error as { code?: string; message?: string } | null}
        disabled={incomplete}
        columns={2}
      >
        <Field label="Role" required className="col-span-2" hint={role?.description ?? 'You can only grant a role whose every permission you hold yourself.'}>
          <Select
            value={roleCode}
            onChange={(e) => setRoleCode(e.target.value)}
            placeholder="Choose a role…"
            options={(roles.data ?? []).map((r) => ({ value: r.code, label: `${r.role_group} · ${r.name}` }))}
          />
        </Field>

        {role?.requires_mfa ? (
          <p className="col-span-2 -mt-2 rounded-[var(--radius-control)] border border-admin/30 bg-admin-ground px-3 py-2 text-small text-admin">
            <span className="font-medium">{role.name}</span> is defined as requiring two-factor authentication. Granting
            it does not switch that on: require MFA on the account itself to enforce it.
          </p>
        ) : null}

        <div className="col-span-2">
          <SectionLabel>Scope</SectionLabel>
          <div className="mt-1.5 flex gap-2">
            {(
              [
                { id: 'LOCATIONS', label: 'Particular locations', hint: 'Reaches each chosen location and everything below it.' },
                { id: 'ORGANIZATION', label: 'The whole organisation', hint: 'Every location, now and in future.' },
              ] as const
            ).map((option) => (
              <button
                key={option.id}
                type="button"
                onClick={() => setScope(option.id)}
                aria-pressed={scope === option.id}
                className={cx(
                  'flex-1 rounded-[var(--radius-control)] border px-3 py-2 text-left transition-colors duration-[120ms]',
                  scope === option.id ? 'border-brand bg-brand-soft' : 'border-line-strong hover:bg-surface-sunken',
                )}
              >
                <span className={cx('block text-body', scope === option.id && 'font-medium text-brand')}>{option.label}</span>
                <span className="block text-small text-ink-muted">{option.hint}</span>
              </button>
            ))}
          </div>
        </div>

        {scope === 'LOCATIONS' ? (
          <div className="col-span-2">
            <div className="flex items-center justify-between gap-3">
              <SectionLabel>Locations ({locationIds.length} chosen)</SectionLabel>
              <Input value={locationTerm} onChange={(e) => setLocationTerm(e.target.value)} placeholder="Filter…" className="w-48" />
            </div>
            <div className="mt-1.5 grid max-h-48 grid-cols-2 gap-x-4 gap-y-1.5 overflow-y-auto rounded-[var(--radius-control)] border border-line p-2">
              {visibleLocations.length === 0 ? (
                <p className="col-span-2 text-small text-ink-muted">No location matches.</p>
              ) : (
                visibleLocations.map((location) => (
                  <Checkbox
                    key={location.id}
                    checked={locationIds.includes(location.id)}
                    onChange={(e) =>
                      setLocationIds((current) =>
                        e.target.checked ? [...current, location.id] : current.filter((id) => id !== location.id),
                      )
                    }
                    label={<span className="code-text">{location.code}</span>}
                    description={`${location.name} · ${location.kind.replace(/_/g, ' ').toLowerCase()}`}
                  />
                ))
              )}
            </div>
            <p className="mt-1 text-small text-ink-muted">
              A Major Locus grant reaches its Minor Loci. It never reaches a sibling.
            </p>
          </div>
        ) : (
          <p className="col-span-2 rounded-[var(--radius-control)] border border-queued/30 bg-queued-ground px-3 py-2 text-small text-queued">
            Organisation-wide is the broadest grant there is. The server allows it only from someone whose own
            authority to grant roles is organisation-wide.
          </p>
        )}

        <Field label="Ends on (optional)" hint="Leave empty for an open-ended grant. Cover and audits should have an end.">
          <Input type="date" value={validTo} onChange={(e) => setValidTo(e.target.value)} min={new Date().toISOString().slice(0, 10)} />
        </Field>

        <Field label="Reason" required hint="Recorded on the grant and in the audit trail.">
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} placeholder="Why does this account need this role?" />
        </Field>
      </Form>
    </Dialog>
  );
}
